import type {Components, Theme} from '@mui/material';
import type {ColorSet} from './tokens/colors';
import {scrollbar as scrollbarSize} from './tokens/spacing';
import {shadow, calendarPickerFilter} from './tokens/effects';
import {duration, easing} from './tokens/animation';

export const createComponentOverrides = (colors: ColorSet): Components<Theme> => ({
  MuiCssBaseline: {
    styleOverrides: {
      '::-webkit-scrollbar': {
        width: scrollbarSize.width,
        height: scrollbarSize.height
      },
      '::-webkit-scrollbar-track': {
        backgroundColor: colors.scrollbar.back
      },
      '::-webkit-scrollbar-thumb': {
        border: `2px solid ${colors.scrollbar.back}`,
        backgroundColor: colors.scrollbar.bar,
        transition: `background-color ${duration.scrollbar} ${easing.default} 0s`
      },
      '::-webkit-calendar-picker-indicator': {
        filter: calendarPickerFilter
      }
    }
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        color: colors.text.primary
      }
    }
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        border: '0',
        backgroundColor: colors.background.paper,
        backgroundImage: 'none !important',
        boxShadow: 'none'
      },
      colorPrimary: {
        color: colors.text.primary
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        border: `1px solid ${colors.divider}`,
        backgroundColor: colors.background.paperTrans,
        backgroundImage: 'none !important',
        boxShadow: 'none'
      }
    }
  },
  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.background.paper,
        backgroundImage: 'none !important',
        boxShadow: shadow.popup(colors.shadow)
      }
    }
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        backgroundColor: `${colors.background.paper} !important`,
        backgroundImage: 'none !important',
        boxShadow: `${shadow.popup(colors.shadow)} !important`
      }
    }
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        backgroundColor: `${colors.background.paper} !important`,
        backgroundImage: 'none !important',
        boxShadow: `${shadow.popup(colors.shadow)} !important`
      }
    }
  },
  MuiList: {
    styleOverrides: {
      root: {
        '&.MuiDataGrid-menuList': {
          backgroundColor: `${colors.background.paper} !important`,
          backgroundImage: 'none !important',
          boxShadow: `${shadow.popup(colors.shadow)} !important`
        }
      }
    }
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.background.paper,
        backgroundImage: 'none !important',
        boxShadow: shadow.popup(colors.shadow)
      }
    }
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.background.paper,
        backgroundImage: 'none !important',
        boxShadow: shadow.dialog(colors.shadow)
      }
    }
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: colors.backdrop
      }
    }
  },
  // @ts-expect-error MUI DatePickers の内部コンポーネント
  MuiPickersPopper: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.background.paper,
        backgroundImage: 'none !important',
        boxShadow: shadow.popup(colors.shadow)
      }
    }
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        backgroundColor: 'inherit',
        borderColor: colors.divider,
        '& .MuiDataGrid-cell': {
          borderColor: colors.divider
        },
        '& .MuiDataGrid-columnHeaders': {
          borderColor: colors.divider
        },
        '& .MuiDataGrid-columnHeader': {
          borderColor: colors.divider
        },
        '& .MuiDataGrid-columnSeparator': {
          color: colors.divider
        },
        '& .MuiDataGrid-overlay': {
          backgroundColor: 'inherit'
        },
        '& .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-pinnedColumns': {
          backgroundColor: colors.background.default,
          borderRightWidth: '1px',
          borderRightStyle: 'solid',
          borderRightColor: colors.divider,
          backgroundImage: 'none !important'
        },
        '& .MuiDataGrid-menuList': {
          backgroundColor: `${colors.background.paper} !important`,
          backgroundImage: 'none !important',
          boxShadow: `${shadow.popup(colors.shadow)} !important`
        },
        '& .MuiDataGrid-menu .MuiPaper-root': {
          backgroundColor: `${colors.background.paper} !important`,
          backgroundImage: 'none !important',
          boxShadow: `${shadow.popup(colors.shadow)} !important`
        },
        '& .MuiPopover-paper[role="presentation"]': {
          backgroundColor: `${colors.background.paper} !important`,
          backgroundImage: 'none !important',
          boxShadow: `${shadow.popup(colors.shadow)} !important`
        }
      }
    }
  }
});
